import React from "react";
import MaterialTable from "material-table";
import tableIcons from "./MaterialTableIcons";
import {ThemeProvider, createTheme} from "@mui/material/styles";
import {
	useGetArmWashesPTD,
	useGetArmStatusPTD,
	useGetArmWashesFYTD,
	useGetArmStatusFYTD,
	useGetArmWashesWTD,
	useGetArmStatusWTD
} from "../../../../queries/ArmData";
import {
	useGetWashRevenue,
	useGetWashRevenueWTD,
	useGetWashRevenuePTD,
	useGetWashRevenueFYTD,
	useGetArmRevenue,
	useGetArmRevenueWTD,
	useGetArmRevenuePTD,
	useGetArmRevenueFYTD
} from '../../../../queries/RevenueData';
import { 
	useGetLaborHoursToday,
	useGetLaborHoursWTD,
	useGetLaborHoursPTD,
	useGetLaborHoursFYTD
} from '../../../../queries/Leaderboards';
import {useGetSitesTable, useGetSiteCodesTable} from "../../../../queries/useGetSites";
import {useSelector} from "react-redux";
import {useGetTargets} from "../../../../queries/useGetTargets";
import "./index.scss";

const FISCAL_YEAR_2017_START = new Date('2017-05-09T00:00:00.000Z');

// Define constants for the fiscal calendar structure
const DAYS_PER_PERIOD = 28;
const PERIODS_PER_YEAR = 13;
const DAYS_PER_YEAR = DAYS_PER_PERIOD * PERIODS_PER_YEAR;

const getFiscalInfo = (targetDate = new Date()) => {
  // Use UTC to prevent timezone issues with the calculations
  const currentTime = targetDate.getTime();
  const startOfBaseYearTime = FISCAL_YEAR_2017_START.getTime();

  // Calculate the total number of days elapsed since the start of FY2017
  const totalDaysElapsed = Math.floor((currentTime - startOfBaseYearTime) / (1000 * 60 * 60 * 24));

  // -------------------------
  // 1. Calculate the Fiscal Year
  // -------------------------
  const fiscalYearOffset = Math.floor(totalDaysElapsed / DAYS_PER_YEAR);
  const fiscalYear = 2017 + fiscalYearOffset;

  // -------------------------
  // 2. Calculate the Start and End of the Fiscal Year
  // -------------------------
  const startOfFiscalYearDate = new Date(startOfBaseYearTime + fiscalYearOffset * DAYS_PER_YEAR * 1000 * 60 * 60 * 24);
  const endOfFiscalYearDate = new Date(startOfFiscalYearDate.getTime() + DAYS_PER_YEAR * 1000 * 60 * 60 * 24 - 1);

  // -------------------------
  // 3. Calculate the Fiscal Period and Days into the year
  // -------------------------
  const daysIntoCurrentFiscalYear = (totalDaysElapsed % DAYS_PER_YEAR) + 1; // Add 1 to make it 1-indexed
  // Periods are 1-indexed, so we add 1
  const fiscalPeriod = Math.floor((daysIntoCurrentFiscalYear - 1) / DAYS_PER_PERIOD) + 1;

  // -------------------------
  // 4. Calculate the Start and End of the Fiscal Period and days into it
  // -------------------------
  const startOfPeriodOffset = (fiscalPeriod - 1) * DAYS_PER_PERIOD;
  const startOfFiscalPeriodDate = new Date(startOfFiscalYearDate.getTime() + startOfPeriodOffset * 1000 * 60 * 60 * 24);
  const endOfFiscalPeriodDate = new Date(startOfFiscalPeriodDate.getTime() + DAYS_PER_PERIOD * 1000 * 60 * 60 * 24 - 1);
  const daysIntoFiscalPeriod = (daysIntoCurrentFiscalYear - 1) % DAYS_PER_PERIOD + 1;

  // -------------------------
  // 5. Calculate the day of the week (Monday = 1, Sunday = 7)
  // -------------------------
  const dayOfWeekRaw = targetDate.getDay(); // 0 = Sunday, 1 = Monday, ...
  const dayOfWeek = dayOfWeekRaw === 0 ? 7 : dayOfWeekRaw;

  // Helper function to format dates as YYYY-MM-DD
  const formatDate = (date: any) => date.toISOString().split('T')[0];

  return {
    fiscalYear: fiscalYear,
    startOfFiscalYear: formatDate(startOfFiscalYearDate),
    endOfFiscalYear: formatDate(endOfFiscalYearDate),
    daysIntoFiscalYear: daysIntoCurrentFiscalYear,
    fiscalPeriod: fiscalPeriod,
    startOfFiscalPeriod: formatDate(startOfFiscalPeriodDate),
    endOfFiscalPeriod: formatDate(endOfFiscalPeriodDate),
    daysIntoFiscalPeriod: daysIntoFiscalPeriod,
    dayOfWeek: dayOfWeek,
  };
};

const theme = createTheme({
	/* your theme configurations */
});

const formatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
});

const midwest_regions: any = {
    // North
    57: 0,
    52: 0,
    53: 0,
    54: 0,
    51: 0,
    59: 0,
    61: 0,
    66: 0,
    70: 0,
    68: 0,
    64: 0,
    67: 0,
    69: 0,
    71: 0,
    74: 0,
    73: 0,
    75: 0,
    76: 0,
    77: 0,
    72: 0,
    // South
    1: 1,
    2: 1,
    3: 1,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
    8: 1,
    9: 1,
    55: 1,
    50: 1,
    56: 1,
    58: 1,
    60: 1,
    62: 1,
    63: 1,
    65: 1,
    10: 1
}

const table_schema_cleanfreak: any[] = [
	{
		title: "Site Code", 
		field: "siteCode",
		render: (rowData: any) => {
			return rowData["siteCode"] === -1 ? " " : rowData["siteCode"]
		},
		customSort: (a: any, b: any) => {
			if(a.siteCode === -1){
				return Number(b.siteCode.slice(2,4)) - 100
			}
			if(b.siteCode === -1){
				return 100 - Number(a.siteCode.slice(2,4))
			}
			return Number(b.siteCode.slice(2,4)) - Number(a.siteCode.slice(2,4))
		},
		defaultSort: "desc"
	},
	{
		title: "Location",
		field: "siteName",
		render: (rowData: any) => {
			return rowData["siteName"].includes("-")
				? rowData["siteName"].split("-")[1]
				: rowData["siteName"];
		},
	},
	{title: "Car Count Daily", field: "carCountToday"},
	{title: "Car Count WTD", field: "carCountWTD", hidden: false},
	{title: "Car Count PTD", field: "carCountPTD", hidden: false},
	{title: "Car Count FYTD", field: "carCountFYTD", hidden: false},
	{
		title: "Daily Car Count Variance %", 
		field: "carCountVarianceDaily",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountToday"] - rowData["carCountTargetToday"]) / rowData["carCountTargetToday"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "WTD Car Count Variance %", 
		field: "carCountVarianceWTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountWTD"] - rowData["carCountTargetWTD"]) / rowData["carCountTargetWTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "PTD Car Count Variance %", 
		field: "carCountVariancePTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountPTD"] - rowData["carCountTargetPTD"]) / rowData["carCountTargetPTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "FYTD Car Count Variance %", 
		field: "carCountVarianceFYTD", 
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountFYTD"] - rowData["carCountTargetFYTD"]) / rowData["carCountTargetFYTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "Wash Revenue Daily", 
		field: "washRevenueToday",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueToday"])
		}
	},
	{
		title: "Wash Revenue WTD", 
		field: "washRevenueWTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueWTD"])
		}
	},
	{
		title: "Wash Revenue PTD", 
		field: "washRevenuePTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenuePTD"])
		}
	},
	{
		title: "Wash Revenue FYTD", 
		field: "washRevenueFYTD", 
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueFYTD"])
		}
	},
	{
		title: "Daily Revenue Variance %", 
		field: "revenueVarianceDaily",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueToday"] - rowData["washRevenueTargetToday"]) / rowData["washRevenueTargetToday"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "WTD Revenue Variance %", 
		field: "revenueVarianceWTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueWTD"] - rowData["washRevenueTargetWTD"]) / rowData["washRevenueTargetWTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "PTD Revenue Variance %", 
		field: "revenueVariancePTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenuePTD"] - rowData["washRevenueTargetPTD"]) / rowData["washRevenueTargetPTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "FYTD Revenue Variance %", 
		field: "revenueVarianceFYTD", 
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueFYTD"] - rowData["washRevenueTargetFYTD"]) / rowData["washRevenueTargetFYTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{title: "Labor Hours Daily", field: "laborDataToday"},
	{title: "Labor Hours WTD", field: "laborDataWTD", hidden: false},
	{title: "Labor Hours PTD", field: "laborDataPTD", hidden: false},
	{title: "Labor Hours FYTD", field: "laborDataFYTD", hidden: false},
	{
		title: "ARM %",
		field: "armPercentage",
		render: (rowData: any) => {
			return `${rowData["armPercentage"]}%`;
		},
	},
	{
		title: "ARM % PTD",
		field: "armPercentagePTD",
		render: (rowData: any) => {
			return `${rowData["armPercentagePTD"]}%`
		}
	},
	{title: "ARM Sold Daily", field: "armSoldToday"},
	{title: "ARM Sold WTD", field: "armSoldWeek"},
	{title: "ARM Sold PTD", field: "armSoldPeriod"},
	{title: "ARM Sold FYTD", field: "armSoldFiscalYear"},
	{
		title: "ARM Conversion",
		field: "armConversion",
		render: (rowData: any) => {
			return `${rowData["armConversion"]}%`;
		},
	},
	{
		title: "ARM Conversion - PTD",
		field: "periodArmConversion",
		render: (rowData: any) => {
			return `${rowData["periodArmConversion"]}%`;
		},
	},
];

const table_schema_rainstorm: any[] = [
	{
		title: "Site Code", 
		field: "siteCode",
		render: (rowData: any) => {
			return rowData["siteCode"] === -1 ? " " : rowData["siteCode"]
		},
		customSort: (a: any, b: any) => {
			if(a.siteCode === -1 && b.siteCode === -1){
				return -1;
			}
			if(a.siteCode === -1){
				if(a.region === 1)
					return Number(b.siteCode.slice(2,4)) - 0;
				else
					return 100;
			}
			if(b.siteCode === -1){
				if(b.region === 1)
					return 0 - Number(a.siteCode.slice(2,4));
				else
					return 100;
			}
			return Number(b.siteCode.slice(2,4)) - Number(a.siteCode.slice(2,4))
		},
		defaultSort: "desc"
	},
	{
		title: "Location",
		field: "siteName",
		render: (rowData: any) => {
			return rowData["siteName"].includes("-")
				? rowData["siteName"].split("-")[1]
				: rowData["siteName"];
		},
	},
	{
		title: "Region", 
		field: "region", 
		render: (rowData: any) => {
			return rowData["region"] === 0 ? "North" : "South"
		},
		defaultSort: "asc"
	},
	{title: "Car Count Daily", field: "carCountToday"},
	{title: "Car Count WTD", field: "carCountWTD", hidden: false},
	{title: "Car Count PTD", field: "carCountPTD", hidden: false},
	{title: "Car Count FYTD", field: "carCountFYTD", hidden: false},
	{title: "Car Count Daily Target", field: "carCountTargetToday"},
	{title: "Car Count WTD Target", field: "carCountTargetWTD", hidden: false},
	{title: "Car Count PTD Target", field: "carCountTargetPTD", hidden: false},
	{title: "Car Count FYTD Target", field: "carCountTargetFYTD", hidden: false},
	{
		title: "Daily Car Count Variance %", 
		field: "carCountVarianceDaily",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountToday"] - rowData["carCountTargetToday"]) / rowData["carCountTargetToday"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "WTD Car Count Variance %", 
		field: "carCountVarianceWTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountWTD"] - rowData["carCountTargetWTD"]) / rowData["carCountTargetWTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "PTD Car Count Variance %", 
		field: "carCountVariancePTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountPTD"] - rowData["carCountTargetPTD"]) / rowData["carCountTargetPTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "FYTD Car Count Variance %", 
		field: "carCountVarianceFYTD", 
		render: (rowData: any) => {
			const variance = 
				(rowData["carCountFYTD"] - rowData["carCountTargetFYTD"]) / rowData["carCountTargetFYTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},

	{
		title: "Wash Revenue Daily", 
		field: "washRevenueToday",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueToday"])
		}
	},
	{
		title: "Wash Revenue WTD", 
		field: "washRevenueWTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueWTD"])
		}
	},
	{
		title: "Wash Revenue PTD", 
		field: "washRevenuePTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenuePTD"])
		}
	},
	{
		title: "Wash Revenue FYTD", 
		field: "washRevenueFYTD", 
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueFYTD"])
		}
	},
	{
		title: "Wash Revenue Target Daily", 
		field: "washRevenueTargetToday",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueTargetToday"])
		}
	},
	{
		title: "Wash Revenue Target WTD", 
		field: "washRevenueTargetWTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueTargetWTD"])
		}
	},
	{
		title: "Wash Revenue Target PTD", 
		field: "washRevenueTargetPTD",
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueTargetPTD"])
		}
	},
	{
		title: "Wash Revenue Target FYTD", 
		field: "washRevenueTargetFYTD", 
		render: (rowData: any) => {
			return formatter.format(rowData["washRevenueTargetFYTD"])
		}
	},

	{
		title: "Daily Revenue Variance %", 
		field: "revenueVarianceDaily",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueToday"] - rowData["washRevenueTargetToday"]) / rowData["washRevenueTargetToday"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "WTD Revenue Variance %", 
		field: "revenueVarianceWTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueWTD"] - rowData["washRevenueTargetWTD"]) / rowData["washRevenueTargetWTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "PTD Revenue Variance %", 
		field: "revenueVariancePTD",
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenuePTD"] - rowData["washRevenueTargetPTD"]) / rowData["washRevenueTargetPTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},
	{
		title: "FYTD Revenue Variance %", 
		field: "revenueVarianceFYTD", 
		render: (rowData: any) => {
			const variance = 
				(rowData["washRevenueFYTD"] - rowData["washRevenueTargetFYTD"]) / rowData["washRevenueTargetFYTD"];
			return <p style={{ color: (variance < 0) ? "red" : "green" }}>
				{
					(Math.round( (variance * 100) * 100 ) / 100)
				}%
			</p>
		}
	},

	{title: "Labor Hours Daily", field: "laborDataToday"},
	{title: "Labor Hours WTD", field: "laborDataWTD", hidden: false},
	{title: "Labor Hours PTD", field: "laborDataPTD", hidden: false},
	{title: "Labor Hours FYTD", field: "laborDataFYTD", hidden: false},
	{
		title: "ARM %",
		field: "armPercentage",
		render: (rowData: any) => {
			return `${rowData["armPercentage"]}%`;
		},
	},
	{
		title: "ARM % PTD",
		field: "armPercentagePTD",
		render: (rowData: any) => {
			return `${rowData["armPercentagePTD"]}%`
		}
	},
	{
		title: "ARM Revenue Daily", 
		field: "armRevenueToday",
		render: (rowData: any) => {
			return formatter.format(rowData["armRevenueToday"])
		}
	},
	{
		title: "ARM Revenue WTD", 
		field: "armRevenueWTD",
		render: (rowData: any) => {
			return formatter.format(rowData["armRevenueWTD"])
		}
	},
	{
		title: "ARM Revenue PTD", 
		field: "armRevenuePTD",
		render: (rowData: any) => {
			return formatter.format(rowData["armRevenuePTD"])
		}
	},
	{
		title: "ARM Revenue FYTD", 
		field: "armRevenueFYTD", 
		render: (rowData: any) => {
			return formatter.format(rowData["armRevenueFYTD"])
		}
	},
	{title: "ARM Sold Daily", field: "armSoldToday"},
	{title: "ARM Sold WTD", field: "armSoldWeek"},
	{title: "ARM Sold PTD", field: "armSoldPeriod"},
	{title: "ARM Sold FYTD", field: "armSoldFiscalYear"},
	{
		title: "ARM Conversion",
		field: "armConversion",
		render: (rowData: any) => {
			return `${rowData["armConversion"]}%`;
		},
	},
	{
		title: "ARM Conversion - PTD",
		field: "periodArmConversion",
		render: (rowData: any) => {
			return `${rowData["periodArmConversion"]}%`;
		},
	},
];


// Step 1
const structureStatusData = (siteData: any, statusData: any) => {
	let dataStruct: any = {};
	let todaysDate: any = new Date();

	for (const armData of statusData) {
		let armSoldToday = 0; // status == 1, today
		let armSoldPeriod = 0; // status == 1, for the month
		let armWashed = 0; // status == 0, today
		let periodArmCars = 0; // status == 0, for the month

		let armDataCast: any = armData;
		let currentDate = new Date(armDataCast["logdate"]);
		let siteCode = armDataCast.site;

		if (dataStruct[siteCode] === undefined) {
			dataStruct[siteCode] = {
				siteCode: siteCode,
				siteName: siteData[siteCode],
				armWashed: 0,
				periodArmCars: 0,
				armSoldPeriod: 0,
				armSoldToday: 0,
			};
		}

		if (
			currentDate.getFullYear() === todaysDate.getFullYear() &&
			currentDate.getMonth() === todaysDate.getMonth() &&
			currentDate.getDate() === todaysDate.getDate()
		) {
			if (armDataCast["status"] === 1) {
				armSoldToday = armDataCast["qty"];
				dataStruct[siteCode]["armSoldToday"] = armSoldToday;
				dataStruct[siteCode]["armSoldPeriod"] =
					dataStruct[siteCode]["armSoldPeriod"] + armSoldToday;
			} else if (armDataCast["status"] === 0) {
				armWashed = armDataCast["qty"];
				dataStruct[siteCode]["armWashed"] = armWashed;
				dataStruct[siteCode]["periodArmCars"] =
					dataStruct[siteCode]["periodArmCars"] + armWashed;
			}
		} else {
			if (armDataCast["status"] === 1) {
				armSoldPeriod = armDataCast["qty"];
				dataStruct[siteCode]["armSoldPeriod"] =
					dataStruct[siteCode]["armSoldPeriod"] + armSoldPeriod;
			} else if (armDataCast["status"] === 0) {
				periodArmCars = armDataCast["qty"];
				dataStruct[siteCode]["periodArmCars"] =
					dataStruct[siteCode]["periodArmCars"] + periodArmCars;
			}
		}
	}

	return dataStruct;
};

// Step 2
const structureWashData = (siteData: any, washData: any, ts:any = "PTD") => {
	let dataStruct: any = {};
	let todaysDate: any = new Date();

	for (const armData of washData) {
		let armDataCast: any = armData;
		let currentDate = new Date(armDataCast["logdate"]);
		let siteCode = armDataCast.site;

		if (dataStruct[siteCode] === undefined) {
			dataStruct[siteCode] = {
				siteCode: siteCode,
				siteName: siteData[siteCode],
				carCountToday: 0,
				carCountPTD: 0,
			};
		}

		if (
			currentDate.getFullYear() === todaysDate.getFullYear() &&
			currentDate.getMonth() === todaysDate.getMonth() &&
			currentDate.getDate() === todaysDate.getDate()
		) {
			dataStruct[siteCode]["carCountToday"] = armDataCast["washes"];
			dataStruct[siteCode][`carCount${ts}`] =
				dataStruct[siteCode][`carCount${ts}`] + armDataCast["washes"];
		} else {
			dataStruct[siteCode][`carCount${ts}`] =
				dataStruct[siteCode][`carCount${ts}`] + armDataCast["washes"];
		}
	}

	return dataStruct;
};

// Step 3
const structureMixedData = (
	siteDict: any, 
	siteCodeDict: any,
	washData: any, 
	statusData: any,
	washDataFYTD: any, 
	statusDataFYTD: any, 
	washDataWTD: any, 
	statusDataWTD: any,
	revenueData: any,
	armRevenueData: any,
	laborData: any,
	targetData: any,
	fiscalInfo: any,
	brand: any
) => {
	let structData: any = [];

	const northernTotals = {
		siteCode: -1,
		siteName: "Total",
		carCountToday: 0,
		carCountPTD: 0,
		carCountWTD: 0,
		carCountFYTD: 0,
		carCountTargetToday: 0,
		carCountTargetPTD: 0,
		carCountTargetWTD: 0,
		carCountTargetFYTD: 0,
		washRevenueToday: 0,
		washRevenueWTD: 0,
		washRevenuePTD: 0,
		washRevenueFYTD: 0,
		washRevenueTargetToday: 0,
		washRevenueTargetWTD: 0,
		washRevenueTargetPTD: 0,
		washRevenueTargetFYTD: 0,
		laborDataToday: 0,
		laborDataWTD: 0,
		laborDataPTD: 0,
		laborDataFYTD: 0,
		armWashed: 0,
		periodArmCars: 0,
		armRevenueToday: 0,
		armRevenueWTD: 0,
		armRevenuePTD: 0,
		armRevenueFYTD: 0,
		armSoldWeek: 0,
		armSoldFiscalYear: 0,
		armSoldPeriod: 0,
		armSoldToday:0,
		armConversion: 0,
		periodArmConversion: 0,
		armPercentage: 0,
		armPercentagePTD: 0,
		brand: brand,
		region: 0
	};

	const southernTotals = {
		siteCode: -1,
		siteName: "Total",
		carCountToday: 0,
		carCountPTD: 0,
		carCountWTD: 0,
		carCountFYTD: 0,
		carCountTargetToday: 0,
		carCountTargetPTD: 0,
		carCountTargetWTD: 0,
		carCountTargetFYTD: 0,
		washRevenueToday: 0,
		washRevenueWTD: 0,
		washRevenuePTD: 0,
		washRevenueFYTD: 0,
		washRevenueTargetToday: 0,
		washRevenueTargetWTD: 0,
		washRevenueTargetPTD: 0,
		washRevenueTargetFYTD: 0,
		laborDataToday: 0,
		laborDataWTD: 0,
		laborDataPTD: 0,
		laborDataFYTD: 0,
		armWashed: 0,
		periodArmCars: 0,
		armRevenueToday: 0,
		armRevenueWTD: 0,
		armRevenuePTD: 0,
		armRevenueFYTD: 0,
		armSoldWeek: 0,
		armSoldFiscalYear: 0,
		armSoldPeriod: 0,
		armSoldToday:0,
		armConversion: 0,
		periodArmConversion: 0,
		armPercentage: 0,
		armPercentagePTD: 0,
		brand: brand,
		region: 1
	}

	const totals = {
		siteCode: -1,
		siteName: "Total",
		carCountToday: 0,
		carCountPTD: 0,
		carCountWTD: 0,
		carCountFYTD: 0,
		carCountTargetToday: 0,
		carCountTargetPTD: 0,
		carCountTargetWTD: 0,
		carCountTargetFYTD: 0,
		washRevenueToday: 0,
		washRevenueWTD: 0,
		washRevenuePTD: 0,
		washRevenueFYTD: 0,
		washRevenueTargetToday: 0,
		washRevenueTargetWTD: 0,
		washRevenueTargetPTD: 0,
		washRevenueTargetFYTD: 0,
		laborDataToday: 0,
		laborDataWTD: 0,
		laborDataPTD: 0,
		laborDataFYTD: 0,
		armWashed: 0,
		periodArmCars: 0,
		armRevenueToday: 0,
		armRevenueWTD: 0,
		armRevenuePTD: 0,
		armRevenueFYTD: 0,
		armSoldWeek: 0,
		armSoldFiscalYear: 0,
		armSoldPeriod: 0,
		armSoldToday:0,
		armConversion: 0,
		periodArmConversion: 0,
		armPercentage: 0,
		armPercentagePTD: 0,
		brand: brand,
		region: -1
	}

	let totals_count = 0;
	let southern_count = 0;
	let northern_count = 0;

	for (const [siteCode, siteInfo] of Object.entries(siteDict)) {
		try {
			let armPercentage =
				statusData[siteCode]["armWashed"] / washData[siteCode]["carCountToday"];
			let armPercentagePTD = 
				statusData[siteCode]["periodArmCars"] / washData[siteCode]["carCountPTD"];
			let armConversion =
				statusData[siteCode]["armSoldToday"] /
				(washData[siteCode]["carCountToday"] -
					statusData[siteCode]["armWashed"]);
			let periodArmConversion =
				statusData[siteCode]["armSoldPeriod"] /
				(washData[siteCode]["carCountPTD"] -
					statusData[siteCode]["periodArmCars"]);

			structData.push({
				siteCode: Number(siteCode) === -1 ? "" : siteCodeDict[Number(siteCode)],
				siteName: siteInfo,

				carCountToday: washData[siteCode]["carCountToday"],
				carCountPTD: washData[siteCode]["carCountPTD"],
				carCountFYTD: washDataFYTD[siteCode]["carCountPTD"],
				carCountWTD: washDataWTD[siteCode]["carCountPTD"],

				carCountTargetToday: targetData[siteCode]["car_count"]["daily"],
				carCountTargetWTD: targetData[siteCode]["car_count"]["wtd"],
				carCountTargetPTD: targetData[siteCode]["car_count"]["ptd"],
				carCountTargetFYTD: Math.round( (targetData[siteCode]["car_count"]["fytd"] * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100,

				washRevenueToday: revenueData[siteCode]["daily"],
				washRevenueWTD: revenueData[siteCode]["wtd"],
				washRevenuePTD: revenueData[siteCode]["ptd"],
				washRevenueFYTD: revenueData[siteCode]["fytd"],

				washRevenueTargetToday: targetData[siteCode]["revenue"]["daily"],
				washRevenueTargetWTD: targetData[siteCode]["revenue"]["wtd"],
				washRevenueTargetPTD: targetData[siteCode]["revenue"]["ptd"],
				washRevenueTargetFYTD: Math.round( ((targetData[siteCode]["revenue"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100,

				laborDataToday: laborData[siteCode]["daily"],
				laborDataWTD: laborData[siteCode]["wtd"],
				laborDataPTD: laborData[siteCode]["ptd"],
				laborDataFYTD: laborData[siteCode]["fytd"],
				armWashed: statusData[siteCode]["armWashed"],
				periodArmCars: statusData[siteCode]["periodArmCars"],
				armRevenueToday: armRevenueData[siteCode]["daily"],
				armRevenueWTD: armRevenueData[siteCode]["wtd"],
				armRevenuePTD: armRevenueData[siteCode]["ptd"],
				armRevenueFYTD: armRevenueData[siteCode]["fytd"],
				armSoldWeek: statusDataWTD[siteCode]["armSoldPeriod"],
				armSoldFiscalYear: statusDataFYTD[siteCode]["armSoldPeriod"],
				armSoldPeriod: statusData[siteCode]["armSoldPeriod"],
				armSoldToday: statusData[siteCode]["armSoldToday"],
				armConversion: isNaN(Math.round(armConversion * 100 * 100) / 100) ? 0 : Math.round(armConversion * 100 * 100) / 100,
				periodArmConversion:
					isNaN(Math.round(periodArmConversion * 100 * 100) / 100) ? 0 : Math.round(periodArmConversion * 100 * 100) / 100,
				armPercentage: isNaN( Math.round(armPercentage * 100 * 100) / 100 ) ? 0 :  Math.round(armPercentage * 100 * 100) / 100,
				armPercentagePTD: isNaN( Math.round(armPercentagePTD * 100 * 100) / 100 ) ? 0 : Math.round(armPercentagePTD * 100 * 100) / 100,
				brand: brand,
				region: midwest_regions[siteCode] // only works for Rainstorm
			});

			if(brand === "cleanfreak"){
				totals["carCountToday"] += washData[siteCode]["carCountToday"]
				totals["carCountPTD"] += washData[siteCode]["carCountPTD"]
				totals["carCountWTD"] += washData[siteCode]["carCountWTD"]
				totals["carCountFYTD"] += washData[siteCode]["carCountFYTD"]

				totals["carCountTargetToday"] += targetData[siteCode]["car_count"]["daily"]
				totals["carCountTargetWTD"] += targetData[siteCode]["car_count"]["wtd"]
				totals["carCountTargetPTD"] += targetData[siteCode]["car_count"]["ptd"]
				totals["carCountTargetFYTD"] += Math.round( ((targetData[siteCode]["car_count"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

				totals["armWashed"] += statusData[siteCode]["armWashed"]
				totals["periodArmCars"] += statusData[siteCode]["periodArmCars"]
				totals["armSoldPeriod"] += statusData[siteCode]["armSoldPeriod"]
				totals["armSoldToday"] += statusData[siteCode]["armSoldToday"]
				totals["washRevenueToday"] += revenueData[siteCode]["daily"]
				totals["washRevenueWTD"] += revenueData[siteCode]["wtd"]
				totals["washRevenuePTD"] += revenueData[siteCode]["ptd"]
				totals["washRevenueFYTD"] += revenueData[siteCode]["fytd"]

				totals["washRevenueTargetToday"] += targetData[siteCode]["revenue"]["daily"]
				totals["washRevenueTargetWTD"] += targetData[siteCode]["revenue"]["wtd"]
				totals["washRevenueTargetPTD"] += targetData[siteCode]["revenue"]["ptd"]
				totals["washRevenueTargetFYTD"] += Math.round( ((targetData[siteCode]["revenue"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

				totals["armRevenueToday"] += armRevenueData[siteCode]["daily"]
				totals["armRevenueWTD"] += armRevenueData[siteCode]["wtd"]
				totals["armRevenuePTD"] += armRevenueData[siteCode]["ptd"]
				totals["armRevenueFYTD"] += armRevenueData[siteCode]["fytd"]
				totals["armConversion"] += isNaN(Math.round(armConversion * 100 * 100) / 100) ? 0 : Math.round(armConversion * 100 * 100) / 100
				totals["periodArmConversion"] += isNaN( Math.round(periodArmConversion * 100 * 100) / 100 ) ? 0 :  Math.round(periodArmConversion * 100 * 100) / 100
				totals["armPercentage"] +=  isNaN( Math.round(armPercentage * 100 * 100) / 100 ) ? 0 : Math.round(armPercentage * 100 * 100) / 100
				totals["armPercentagePTD"] +=  isNaN( Math.round(armPercentagePTD * 100 * 100) / 100 ) ? 0 : Math.round(armPercentagePTD * 100 * 100) / 100

				totals["washRevenueTargetToday"] = Math.round(totals["washRevenueTargetToday"] * 100) / 100
				totals["washRevenueTargetWTD"] = Math.round(totals["washRevenueTargetWTD"] * 100) / 100
				totals["washRevenueTargetPTD"] = Math.round(totals["washRevenueTargetPTD"] * 100) / 100
				totals["washRevenueTargetFYTD"] = Math.round(totals["washRevenueTargetFYTD"] * 100) / 100

				totals["carCountTargetToday"] = Math.round(totals["carCountTargetToday"] * 100) / 100
				totals["carCountTargetWTD"] = Math.round(totals["carCountTargetWTD"] * 100) / 100
				totals["carCountTargetPTD"] = Math.round(totals["carCountTargetPTD"] * 100) / 100
				totals["carCountTargetFYTD"] = Math.round(totals["carCountTargetFYTD"] * 100) / 100

				totals_count += 1;

			}else{
				if(midwest_regions[siteCode] === 0){
					northernTotals["carCountToday"] += washData[siteCode]["carCountToday"]
					northernTotals["carCountFYTD"] += washDataFYTD[siteCode]["carCountPTD"]
					northernTotals["carCountWTD"] += washDataWTD[siteCode]["carCountPTD"]
					northernTotals["carCountPTD"] += washData[siteCode]["carCountPTD"]

					northernTotals["carCountTargetToday"] += targetData[siteCode]["car_count"]["daily"]
					northernTotals["carCountTargetWTD"] += targetData[siteCode]["car_count"]["wtd"]
					northernTotals["carCountTargetPTD"] += targetData[siteCode]["car_count"]["ptd"]
					northernTotals["carCountTargetFYTD"] += Math.round( ((targetData[siteCode]["car_count"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

					northernTotals["armWashed"] += statusData[siteCode]["armWashed"]
					northernTotals["periodArmCars"] += statusData[siteCode]["periodArmCars"]
					northernTotals["armSoldPeriod"] += statusData[siteCode]["armSoldPeriod"]
					northernTotals["armSoldToday"] += statusData[siteCode]["armSoldToday"]
					northernTotals["armSoldWeek"] += statusDataWTD[siteCode]["armSoldPeriod"]
					northernTotals["armSoldFiscalYear"] += statusDataFYTD[siteCode]["armSoldPeriod"]

					northernTotals["washRevenueToday"] += revenueData[siteCode]["daily"]
					northernTotals["washRevenueWTD"] += revenueData[siteCode]["wtd"]
					northernTotals["washRevenuePTD"] += revenueData[siteCode]["ptd"]
					northernTotals["washRevenueFYTD"] += revenueData[siteCode]["fytd"]

					northernTotals["washRevenueTargetToday"] += targetData[siteCode]["revenue"]["daily"]
					northernTotals["washRevenueTargetWTD"] += targetData[siteCode]["revenue"]["wtd"]
					northernTotals["washRevenueTargetPTD"] += targetData[siteCode]["revenue"]["ptd"]
					northernTotals["washRevenueTargetFYTD"] += Math.round( ((targetData[siteCode]["revenue"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

					northernTotals["armRevenueToday"] += armRevenueData[siteCode]["daily"]
					northernTotals["armRevenueWTD"] += armRevenueData[siteCode]["wtd"]
					northernTotals["armRevenuePTD"] += armRevenueData[siteCode]["ptd"]
					northernTotals["armRevenueFYTD"] += armRevenueData[siteCode]["fytd"]

					northernTotals["laborDataToday"] += laborData[siteCode]["daily"]
					northernTotals["laborDataWTD"] += laborData[siteCode]["wtd"]
					northernTotals["laborDataPTD"] += laborData[siteCode]["ptd"]
					northernTotals["laborDataFYTD"] += laborData[siteCode]["fytd"]

					northernTotals["washRevenueToday"] = Math.round(northernTotals["washRevenueToday"] * 100) / 100
					northernTotals["washRevenueWTD"] = Math.round(northernTotals["washRevenueWTD"] * 100) / 100
					northernTotals["washRevenuePTD"] = Math.round(northernTotals["washRevenuePTD"] * 100) / 100
					northernTotals["washRevenueFYTD"] = Math.round(northernTotals["washRevenueFYTD"] * 100) / 100

					northernTotals["washRevenueTargetToday"] = Math.round(northernTotals["washRevenueTargetToday"] * 100) / 100
					northernTotals["washRevenueTargetWTD"] = Math.round(northernTotals["washRevenueTargetWTD"] * 100) / 100
					northernTotals["washRevenueTargetPTD"] = Math.round(northernTotals["washRevenueTargetPTD"] * 100) / 100
					northernTotals["washRevenueTargetFYTD"] = Math.round(northernTotals["washRevenueTargetFYTD"] * 100) / 100

					northernTotals["carCountTargetToday"] = Math.round(northernTotals["carCountTargetToday"] * 100) / 100
					northernTotals["carCountTargetWTD"] = Math.round(northernTotals["carCountTargetWTD"] * 100) / 100
					northernTotals["carCountTargetPTD"] = Math.round(northernTotals["carCountTargetPTD"] * 100) / 100
					northernTotals["carCountTargetFYTD"] = Math.round(northernTotals["carCountTargetFYTD"] * 100) / 100

					northernTotals["armRevenueToday"] = Math.round(northernTotals["armRevenueToday"] * 100) / 100
					northernTotals["armRevenueWTD"] = Math.round(northernTotals["armRevenueWTD"] * 100) / 100
					northernTotals["armRevenuePTD"] = Math.round(northernTotals["armRevenuePTD"] * 100) / 100
					northernTotals["armRevenueFYTD"] = Math.round(northernTotals["armRevenueFYTD"] * 100) / 100

					northernTotals["laborDataToday"] = Math.round(northernTotals["laborDataToday"] * 100) / 100
					northernTotals["laborDataWTD"] = Math.round(northernTotals["laborDataWTD"] * 100) / 100
					northernTotals["laborDataPTD"] = Math.round(northernTotals["laborDataPTD"] * 100) / 100
					northernTotals["laborDataFYTD"] = Math.round(northernTotals["laborDataFYTD"] * 100) / 100

					northernTotals["armConversion"] += isNaN(Math.round(armConversion * 100 * 100) / 100) ? 0 : Math.round(armConversion * 100 * 100) / 100
					northernTotals["periodArmConversion"] += isNaN( Math.round(periodArmConversion * 100 * 100) / 100 ) ? 0 :  Math.round(periodArmConversion * 100 * 100) / 100
					northernTotals["armPercentage"] +=  isNaN( Math.round(armPercentage * 100 * 100) / 100 ) ? 0 : Math.round(armPercentage * 100 * 100) / 100
					northernTotals["armPercentagePTD"] +=  isNaN( Math.round(armPercentagePTD * 100 * 100) / 100 ) ? 0 : Math.round(armPercentagePTD * 100 * 100) / 100

					northern_count += 1;
				}else{
					southernTotals["carCountToday"] += washData[siteCode]["carCountToday"]
					southernTotals["carCountFYTD"] += washDataFYTD[siteCode]["carCountPTD"]
					southernTotals["carCountWTD"] += washDataWTD[siteCode]["carCountPTD"]
					southernTotals["carCountPTD"] += washData[siteCode]["carCountPTD"]

					southernTotals["carCountTargetToday"] += targetData[siteCode]["car_count"]["daily"]
					southernTotals["carCountTargetWTD"] += targetData[siteCode]["car_count"]["wtd"]
					southernTotals["carCountTargetPTD"] += targetData[siteCode]["car_count"]["ptd"]
					southernTotals["carCountTargetFYTD"] += Math.round( ((targetData[siteCode]["car_count"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

					southernTotals["washRevenueToday"] += revenueData[siteCode]["daily"]
					southernTotals["washRevenueWTD"] += revenueData[siteCode]["wtd"]
					southernTotals["washRevenuePTD"] += revenueData[siteCode]["ptd"]
					southernTotals["washRevenueFYTD"] += revenueData[siteCode]["fytd"]

					southernTotals["washRevenueTargetToday"] += targetData[siteCode]["revenue"]["daily"]
					southernTotals["washRevenueTargetWTD"] += targetData[siteCode]["revenue"]["wtd"]
					southernTotals["washRevenueTargetPTD"] += targetData[siteCode]["revenue"]["ptd"]
					southernTotals["washRevenueTargetFYTD"] += Math.round( ((targetData[siteCode]["revenue"]["fytd"]) * (fiscalInfo.daysIntoFiscalYear / 365)) * 100 ) / 100

					southernTotals["armWashed"] += statusData[siteCode]["armWashed"]
					southernTotals["periodArmCars"] += statusData[siteCode]["periodArmCars"]
					southernTotals["armSoldWeek"] += statusDataWTD[siteCode]["armSoldPeriod"]
					southernTotals["armSoldFiscalYear"] += statusDataFYTD[siteCode]["armSoldPeriod"]
					southernTotals["armSoldPeriod"] += statusData[siteCode]["armSoldPeriod"]
					southernTotals["armSoldToday"] += statusData[siteCode]["armSoldToday"]

					southernTotals["armRevenueToday"] += armRevenueData[siteCode]["daily"]
					southernTotals["armRevenueWTD"] += armRevenueData[siteCode]["wtd"]
					southernTotals["armRevenuePTD"] += armRevenueData[siteCode]["ptd"]
					southernTotals["armRevenueFYTD"] += armRevenueData[siteCode]["fytd"]

					southernTotals["laborDataToday"] += laborData[siteCode]["daily"]
					southernTotals["laborDataWTD"] += laborData[siteCode]["wtd"]
					southernTotals["laborDataPTD"] += laborData[siteCode]["ptd"]
					southernTotals["laborDataFYTD"] += laborData[siteCode]["fytd"]

					southernTotals["washRevenueToday"] = Math.round(southernTotals["washRevenueToday"] * 100) / 100
					southernTotals["washRevenueWTD"] = Math.round(southernTotals["washRevenueWTD"] * 100) / 100
					southernTotals["washRevenuePTD"] = Math.round(southernTotals["washRevenuePTD"] * 100) / 100
					southernTotals["washRevenueFYTD"] = Math.round(southernTotals["washRevenueFYTD"] * 100) / 100

					southernTotals["washRevenueTargetToday"] = Math.round(southernTotals["washRevenueTargetToday"] * 100) / 100
					southernTotals["washRevenueTargetWTD"] = Math.round(southernTotals["washRevenueTargetWTD"] * 100) / 100
					southernTotals["washRevenueTargetPTD"] = Math.round(southernTotals["washRevenueTargetPTD"] * 100) / 100
					southernTotals["washRevenueTargetFYTD"] = Math.round(southernTotals["washRevenueTargetFYTD"] * 100) / 100

					southernTotals["carCountTargetToday"] = Math.round(southernTotals["carCountTargetToday"] * 100) / 100
					southernTotals["carCountTargetWTD"] = Math.round(southernTotals["carCountTargetWTD"] * 100) / 100
					southernTotals["carCountTargetPTD"] = Math.round(southernTotals["carCountTargetPTD"] * 100) / 100
					southernTotals["carCountTargetFYTD"] = Math.round(southernTotals["carCountTargetFYTD"] * 100) / 100

					southernTotals["armRevenueToday"] = Math.round(southernTotals["armRevenueToday"] * 100) / 100
					southernTotals["armRevenueWTD"] = Math.round(southernTotals["armRevenueWTD"] * 100) / 100
					southernTotals["armRevenuePTD"] = Math.round(southernTotals["armRevenuePTD"] * 100) / 100
					southernTotals["armRevenueFYTD"] = Math.round(southernTotals["armRevenueFYTD"] * 100) / 100

					southernTotals["laborDataToday"] = Math.round(southernTotals["laborDataToday"] * 100) / 100
					southernTotals["laborDataWTD"] = Math.round(southernTotals["laborDataWTD"] * 100) / 100
					southernTotals["laborDataPTD"] = Math.round(southernTotals["laborDataPTD"] * 100) / 100
					southernTotals["laborDataFYTD"] = Math.round(southernTotals["laborDataFYTD"] * 100) / 100

					southernTotals["armConversion"] += isNaN(Math.round(armConversion * 100 * 100) / 100) ? 0 : Math.round(armConversion * 100 * 100) / 100
					southernTotals["periodArmConversion"] += isNaN( Math.round(periodArmConversion * 100 * 100) / 100 ) ? 0 :  Math.round(periodArmConversion * 100 * 100) / 100
					southernTotals["armPercentage"] +=  isNaN( Math.round(armPercentage * 100 * 100) / 100 ) ? 0 : Math.round(armPercentage * 100 * 100) / 100
					southernTotals["armPercentagePTD"] +=  isNaN( Math.round(armPercentagePTD * 100 * 100) / 100 ) ? 0 : Math.round(armPercentagePTD * 100 * 100) / 100

					southern_count += 1;
				}
			}

		} catch {}
	}

	if(brand === "cleanfreak"){

		totals["armConversion"] 		= Math.round( (totals["armConversion"] / totals_count) * 100 ) / 100
		totals["periodArmConversion"] 	= Math.round( (totals["periodArmConversion"] / totals_count) * 100 ) / 100
		totals["armPercentage"] 		= Math.round( (totals["armPercentage"] / totals_count) * 100 ) / 100
		totals["armPercentagePTD"] 		= Math.round( (totals["armPercentagePTD"] / totals_count) * 100 ) / 100

		structData.push(totals);
	}else{

		southernTotals["armConversion"] 		= Math.round( (southernTotals["armConversion"] / southern_count) * 100 ) / 100
		southernTotals["periodArmConversion"] 	= Math.round( (southernTotals["periodArmConversion"] / southern_count) * 100 ) / 100
		southernTotals["armPercentage"] 		= Math.round( (southernTotals["armPercentage"] / southern_count) * 100 ) / 100
		southernTotals["armPercentagePTD"] 		= Math.round( (southernTotals["armPercentagePTD"] / southern_count) * 100 ) / 100

		structData.push(southernTotals);

		northernTotals["armConversion"] 		= Math.round( (northernTotals["armConversion"] / northern_count) * 100 ) / 100
		northernTotals["periodArmConversion"] 	= Math.round( (northernTotals["periodArmConversion"] / northern_count) * 100 ) / 100
		northernTotals["armPercentage"] 		= Math.round( (northernTotals["armPercentage"] / northern_count) * 100 ) / 100
		northernTotals["armPercentagePTD"] 		= Math.round( (northernTotals["armPercentagePTD"] / northern_count) * 100 ) / 100

		structData.push(northernTotals);
	}

	return structData;
};

const structureRevenueData = (
	revenueData: any,
	revenueDataWTD: any,
	revenueDataPTD: any,
	revenueDataFYTD: any,
	siteDict: any
) => {
	const returnData: any = {};

	for (const [siteCode, siteInfo] of Object.entries(siteDict)) {
		returnData[siteCode] = {
			"ptd": 0,
			"wtd": 0,
			"fytd": 0,
			"daily": 0
		}
	}

	for (const [index, siteData] of Object.entries(revenueData)) {
		try{
			const revenueData: any = siteData;
			returnData[revenueData.site]["daily"] = (Math.round(revenueData.revenue * 100) / 100);
		}catch{
	
		}
	}

	for (const [index, siteData] of Object.entries(revenueDataWTD)) {
		try{
			const revenueData: any = siteData;
			returnData[revenueData.site]["wtd"] = (Math.round(revenueData.revenue * 100) / 100);
		}catch{
	
		}
	}

	for (const [index, siteData] of Object.entries(revenueDataPTD)) {
		try{
			const revenueData: any = siteData;
			returnData[revenueData.site]["ptd"] = (Math.round(revenueData.revenue * 100) / 100);
		}catch{
	
		}
	}

	for (const [index, siteData] of Object.entries(revenueDataFYTD)) {
		try{
			const revenueData: any = siteData;
			returnData[revenueData.site]["fytd"] = (Math.round(revenueData.revenue * 100) / 100);
		}catch{
	
		}
	}

	return returnData;
}

const structureLaborData = (
	laborData: any,
	laborDataWTD: any,
	laborDataPTD: any,
	laborDataFYTD: any,
	siteDict: any
) => {
	const returnData: any = {};

	for (const [siteCode, siteInfo] of Object.entries(siteDict)) {
		returnData[siteCode] = {
			"ptd": 0,
			"wtd": 0,
			"fytd": 0,
			"daily": 0
		}
	}

	for (const [sitecode, hours] of Object.entries(laborData.all_hours)) {
		try{
			returnData[Number(sitecode)]["daily"] = (Math.round(Number(hours) * 100) / 100);
		}catch {

		}
	}

	for (const [sitecode, hours] of Object.entries(laborDataWTD.all_hours)) {
		try{
			returnData[Number(sitecode)]["wtd"] = (Math.round(Number(hours) * 100) / 100);
		}catch{

		}
	}

	for (const [sitecode, hours] of Object.entries(laborDataPTD.all_hours)) {
		try{
			returnData[Number(sitecode)]["ptd"] = (Math.round(Number(hours) * 100) / 100);
		}catch{

		}
	}

	for (const [sitecode, hours] of Object.entries(laborDataFYTD.all_hours)) {
		try{
			returnData[Number(sitecode)]["fytd"] = (Math.round(Number(hours) * 100) / 100);
		}catch{

		}
	}

	return returnData;
}

const structureTargetData = (targets: any, siteDict: any, periodData: any) => {
	const returnData: any = {};
	for (const [siteCode, siteInfo] of Object.entries(siteDict)) {
		returnData[siteCode] = {};
		returnData[siteCode]["car_count"] = {
			"ptd": 0,
			"wtd": 0,
			"fytd": 0,
			"daily": 0
		}
		returnData[siteCode]["revenue"] = {
			"ptd": 0,
			"wtd": 0,
			"fytd": 0,
			"daily": 0
		}
	}

	for(const data of targets){
		if(data.period === periodData.fiscalPeriod){
			returnData[data.site_number][data.target_name]["ptd"] = Math.round( ((data.target_value) * (periodData.daysIntoFiscalPeriod / 28)) * 100 ) / 100;
			returnData[data.site_number][data.target_name]["wtd"] = Math.round( ((data.target_value / 4) * (periodData.dayOfWeek / 7)) * 100 ) / 100;
			returnData[data.site_number][data.target_name]["daily"] = Math.round( (data.target_value / 28) * 100 ) / 100;
		}
		returnData[data.site_number][data.target_name]["fytd"] += data.target_value;
		returnData[data.site_number][data.target_name]["fytd"] = Math.round( (returnData[data.site_number][data.target_name]["fytd"]) * 100 ) / 100;
	}

	return returnData;
}

const RevenueDashboard = ({
	brand = "rainstorm"
}) => {
	const selectedFilter = useSelector((state: any) => state.filter);

	const {
		data: armWashData,
		isFetching: isFetchingArmWashData,
		isError: isErrorArmWashData,
	} = useGetArmWashesPTD();

	const {
		data: armStatusData,
		isFetching: isFetchingArmStatusData,
		isError: isErrorArmStatusData,
	} = useGetArmStatusPTD();

	const {
		data: armWashDataFYTD,
		isFetching: isFetchingArmWashDataFYTD,
		isError: isErrorArmWashDataFYTD,
	} = useGetArmWashesFYTD();

	const {
		data: armStatusDataFYTD,
		isFetching: isFetchingArmStatusDataFYTD,
		isError: isErrorArmStatusDataFYTD,
	} = useGetArmStatusFYTD();

	const {
		data: armWashDataWTD,
		isFetching: isFetchingArmWashDataWTD,
		isError: isErrorArmWashDataWTD,
	} = useGetArmWashesWTD();

	const {
		data: armStatusDataWTD,
		isFetching: isFetchingArmStatusDataWTD,
		isError: isErrorArmStatusDataWTD,
	} = useGetArmStatusWTD();

	const {
		data: washData,
		isFetching: washDataFetching,
		isError: washDataError
	} = useGetWashRevenue({
		start_date: `${selectedFilter["endDate"]}`,
		end_date: `${selectedFilter["endDate"]}`,
		plans: selectedFilter["plan"],
		sites: selectedFilter["location"]
	})

	const {
		data: armRevenue,
		isFetching: armRevenueFetching,
		isError: armRevenueError
	} = useGetArmRevenue({
		start_date: `${selectedFilter["endDate"]}`,
		end_date: `${selectedFilter["endDate"]}`,
		plans: selectedFilter["plan"],
		sites: selectedFilter["location"]
	})

	const {
		data: armRevenueWTD,
		isFetching: armRevenueWTDFetching,
		isError: armRevenueWTDError
	} = useGetArmRevenueWTD({
		start_date: `${selectedFilter["endDate"]}`,
		end_date: `${selectedFilter["endDate"]}`,
		plans: selectedFilter["plan"],
		sites: selectedFilter["location"]
	})

	const {
		data: armRevenuePTD,
		isFetching: armRevenuePTDFetching,
		isError: armRevenuePTDError
	} = useGetArmRevenuePTD({
		start_date: `${selectedFilter["endDate"]}`,
		end_date: `${selectedFilter["endDate"]}`,
		plans: selectedFilter["plan"],
		sites: selectedFilter["location"]
	})

	const {
		data: armRevenueFYTD,
		isFetching: armRevenueFYTDFetching,
		isError: armRevenueFYTDError
	} = useGetArmRevenueFYTD({
		start_date: `${selectedFilter["endDate"]}`,
		end_date: `${selectedFilter["endDate"]}`,
		plans: selectedFilter["plan"],
		sites: selectedFilter["location"]
	})

	const {
		data: washDataWTD,
		isFetching: washDataFetchingWTD,
		isError: washDataErrorWTD
	} = useGetWashRevenueWTD()


	const {
		data: washDataPTD,
		isFetching: washDataFetchingPTD,
		isError: washDataErrorPTD
	} = useGetWashRevenuePTD()

	const {
		data: washDataFYTD,
		isFetching: washDataFetchingFYTD,
		isError: washDataErrorFYTD
	} = useGetWashRevenueFYTD()

	const {
		data: siteData,
		isFetching: isFetchingSites,
		isError: isErrorSites,
	} = useGetSitesTable();

	const {
		data: siteDataAlt,
		isFetching: isFetchingSitesAlt,
		isError: isErrorSitesAlt
	} = useGetSiteCodesTable();

	const {
		data: laborData,
		isFetching: laborDataFetching,
		isError: isErrorLaborData
	} = useGetLaborHoursToday();

	const {
		data: laborDataWTD,
		isFetching: laborDataFetchingWTD,
		isError: isErrorLaborDataWTD
	} = useGetLaborHoursWTD();

	const {
		data: laborDataPTD,
		isFetching: laborDataFetchingPTD,
		isError: isErrorLaborDataPTD
	} = useGetLaborHoursPTD();

	const {
		data: laborDataFYTD,
		isFetching: laborDataFetchingFYTD,
		isError: isErrorLaborDataFYTD
	} = useGetLaborHoursFYTD();

	const fiscalInfo = getFiscalInfo();
	const {
		data: targetData, 
		isFetching: isFetchingTargets,
		isError: isErrorTargetes,
		refetch: refetchTargets
	} = useGetTargets(
		brand,
		fiscalInfo.fiscalYear,
		["revenue", "car_count"]
	);

	// This is only true when the data has not been rendered for the first time.
	// Refetch for armWashData and siteData will happen every minute, the table needs to remain
	// displayed while deta is fetching.

	if (
		(
			isFetchingArmWashData || isFetchingArmStatusData || isFetchingSites || isFetchingArmWashDataFYTD
			|| isFetchingArmStatusDataWTD || isFetchingArmWashDataWTD || isFetchingArmStatusDataFYTD 
			|| washDataFetchingWTD || washDataFetchingPTD || washDataFetchingFYTD || washDataFetching || isFetchingSitesAlt
			|| armRevenueFetching || armRevenueWTDFetching || armRevenuePTDFetching || armRevenueFYTDFetching
			|| laborDataFetching || laborDataFetchingWTD || laborDataFetchingPTD || laborDataFetchingFYTD || isFetchingTargets
		) &&
		(
			!armStatusData || !armWashData || !siteData || !armStatusDataWTD 
			|| !armWashDataWTD || !armStatusDataFYTD || !armWashDataFYTD
			|| !washData || !washDataWTD || !washDataPTD || !washDataFYTD || !siteDataAlt
			|| !armRevenue || !armRevenueWTD || !armRevenuePTD || !armRevenueFYTD
			|| !laborData || !laborDataWTD || !laborDataPTD || !laborDataFYTD || !targetData
		)
	)
		return <>Loading...</>;

	let armStatus = structureStatusData(siteData, armStatusData);
	let armWashes = structureWashData(siteData, armWashData);
	let armStatusWtd = structureStatusData(siteData, armStatusDataWTD);
	let armWashesWtd = structureWashData(siteData, armWashDataWTD);
	let armStatusFytd = structureStatusData(siteData, armStatusDataFYTD);
	let armWashesFytd = structureWashData(siteData, armWashDataFYTD);

	return (
		<>
			<ThemeProvider theme={theme}>
				<MaterialTable
					title={"ARM & Wash Revenue Dashboard"}
					data={
						structureMixedData(
							siteData, 
							siteDataAlt,
							armWashes, 
							armStatus, 
							armWashesFytd,
							armStatusFytd,
							armWashesWtd,
							armStatusWtd,
							structureRevenueData(
								washData,
								washDataWTD,
								washDataPTD,
								washDataFYTD,
								siteData
							),
							structureRevenueData(
								armRevenue,
								armRevenueWTD,
								armRevenuePTD,
								armRevenueFYTD,
								siteData
							),
							structureLaborData(
								laborData,
								laborDataWTD,
								laborDataPTD,
								laborDataFYTD,
								siteData
							),
							structureTargetData(
								targetData?.get_targets,
								siteData, 
								fiscalInfo
							),
							fiscalInfo,
							brand
						)
					}
					columns={ brand === 'cleanfreak' ? table_schema_cleanfreak : table_schema_rainstorm }
					options={{
						selection: false,
						headerStyle: {
							backgroundColor: brand === 'cleanfreak' ? "red" : "blue",
							color: "#FFF",
							fontSize: 14,
							fontWeight: 1000,
						},
						pageSize: 120,
						pageSizeOptions: [],
						paging: false,
						sorting: true,
						columnsButton: true,
						search: false,
						exportButton: true
					}}
					icons={tableIcons}
				/>
			</ThemeProvider>
			<br/>
			<h2>Fiscal Period: {fiscalInfo.fiscalPeriod} Fiscal Year: {fiscalInfo.fiscalYear}</h2><br/>
			<h2>Fiscal Period Start: {fiscalInfo.startOfFiscalPeriod} Fiscal Period End: {fiscalInfo.endOfFiscalPeriod}</h2><br/>
			<h2>Fiscal Year Start: {fiscalInfo.startOfFiscalYear} Fiscal Year End: {fiscalInfo.endOfFiscalYear}</h2><br/>
		</>
	);
};

export default RevenueDashboard;
